import configs, {buildApiRequestHeaders} from '../configs';
import {
    getTsunamiMarkerLevel,
    getTsunamiMarkerWarningRank,
    getTsunamiWarningTypeLabel,
    getTsunamiWarningTypeRank
} from '../helpers/helpers-tsunami';

function parseTsunamiPayload(payload) {
    if (Array.isArray(payload)) {
        return payload;
    }
    if (payload && typeof payload.body === 'string') {
        try {
            const parsedBody = JSON.parse(payload.body);

            return Array.isArray(parsedBody) ? parsedBody : [];
        } catch (error) {
            console.warn('津波APIレスポンス body の JSON 解析に失敗しました', error);
            return [];
        }
    }
    return [];
}

function toFiniteNumber(value) {
    const number = Number(value);

    return Number.isFinite(number) ? number : null;
}

function parseTsunamiDate(value) {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
}

function buildTsunamiRequestUrl() {
    const url = new URL(configs.apiUrl.tsunami, window.location.href);

    url.searchParams.set('_', String(Date.now()));
    return url.toString();
}

function sortStations(a, b) {
    const aObserveTime = parseTsunamiDate(a.observetime),
        bObserveTime = parseTsunamiDate(b.observetime),
        aHeightValue = Number.isFinite(a.heightValue) ? a.heightValue : -1,
        bHeightValue = Number.isFinite(b.heightValue) ? b.heightValue : -1;

    if (b.markerLevel !== a.markerLevel) {
        return b.markerLevel - a.markerLevel;
    }
    if (bHeightValue !== aHeightValue) {
        return bHeightValue - aHeightValue;
    }
    if (aObserveTime && bObserveTime) {
        return bObserveTime - aObserveTime;
    }
    if (bObserveTime) {
        return 1;
    }
    if (aObserveTime) {
        return -1;
    }
    return String(a.stationname || '').localeCompare(String(b.stationname || ''), 'ja');
}

function sortTsunamis(a, b) {
    const aDate = parseTsunamiDate(a.report_datetime),
        bDate = parseTsunamiDate(b.report_datetime);

    if (aDate && bDate) {
        return bDate - aDate;
    }
    if (bDate) {
        return 1;
    }
    if (aDate) {
        return -1;
    }
    return String(b.eventid || '').localeCompare(String(a.eventid || ''));
}

function normalizeStation(record) {
    const latitude = toFiniteNumber(record.latitude),
        longitude = toFiniteNumber(record.longitude),
        heightValue = toFiniteNumber(record.heightValue ?? record.height),
        warningRank = getTsunamiWarningTypeRank(record.wavetypename),
        markerLevel = getTsunamiMarkerLevel({
            markerLevel: record.markerLevel,
            heightValue,
            height: record.height,
            wavetypename: record.wavetypename
        });

    return {
        eventid: String(record.eventid || ''),
        serial: String(record.serial || ''),
        areacode: String(record.areacode || ''),
        areaname: record.areaname || '',
        stationcode: String(record.stationcode || ''),
        stationname: record.stationname || '',
        height: record.height || '',
        height_desc: record.height_desc || '',
        heightValue,
        observetime: record.observetime || '',
        arrivaltime: record.arrivaltime || '',
        report_datetime: record.report_datetime || '',
        title: record.title || '',
        headline: record.headline || '',
        wavetypename: record.wavetypename || '',
        warningRank,
        latitude,
        longitude,
        hasCoordinate: Boolean(record.hasCoordinate) && latitude !== null && longitude !== null,
        markerLevel
    };
}

function normalizeCauseEarthquake(record) {
    const causeEarthquake = record && record.causeEarthquake;

    if (!causeEarthquake || typeof causeEarthquake !== 'object') {
        return null;
    }

    const hypocenterLatitude = toFiniteNumber(causeEarthquake.hypocenterLatitude),
        hypocenterLongitude = toFiniteNumber(causeEarthquake.hypocenterLongitude),
        normalized = {
            eventid: String(causeEarthquake.eventid || record.eventid || ''),
            hypocentername: causeEarthquake.hypocentername || '',
            hypocenterLatitude,
            hypocenterLongitude,
            hasHypocenterCoordinate: hypocenterLatitude !== null && hypocenterLongitude !== null,
            magnitude: causeEarthquake.magnitude || ''
        };

    if (
        !normalized.hypocentername &&
        !normalized.magnitude &&
        !normalized.hasHypocenterCoordinate
    ) {
        return null;
    }

    return normalized;
}

function getRepresentativeWarningType(stations) {
    const representativeStation = stations.reduce((bestStation, station) => {
        if (!bestStation) {
            return station;
        }

        const stationRank = Math.max(getTsunamiWarningTypeRank(station.wavetypename), getTsunamiMarkerWarningRank(station.markerLevel)),
            bestRank = Math.max(getTsunamiWarningTypeRank(bestStation.wavetypename), getTsunamiMarkerWarningRank(bestStation.markerLevel));

        if (stationRank !== bestRank) {
            return stationRank > bestRank ? station : bestStation;
        }
        return sortStations(station, bestStation) < 0 ? station : bestStation;
    }, null);

    if (!representativeStation) {
        return '津波情報';
    }

    return getTsunamiWarningTypeLabel(
        representativeStation.wavetypename,
        representativeStation.markerLevel
    );
}

function buildMaxHeightDisplay(stations) {
    const stationWithHeight = stations.find(station => Number.isFinite(station.heightValue));

    if (!stationWithHeight) {
        return '-';
    }
    if (stationWithHeight.height_desc) {
        return stationWithHeight.height_desc;
    }
    return `${stationWithHeight.heightValue}m`;
}

function normalizeTsunamiEvent(records) {
    const stations = records.map(normalizeStation).sort(sortStations),
        firstRecord = records[0] || {},
        causeEarthquake = records.map(normalizeCauseEarthquake).find(Boolean),
        eventid = String(firstRecord.eventid || ''),
        reportDateCandidates = records
            .map(record => record.report_datetime)
            .filter(Boolean)
            .sort((a, b) => {
                const aDate = parseTsunamiDate(a),
                    bDate = parseTsunamiDate(b);

                if (aDate && bDate) {
                    return bDate - aDate;
                }
                if (bDate) {
                    return 1;
                }
                if (aDate) {
                    return -1;
                }
                return 0;
            });

    return {
        eventid,
        serial: String(firstRecord.serial || ''),
        title: firstRecord.title || '',
        headline: firstRecord.headline || '',
        report_datetime: reportDateCandidates[0] || firstRecord.report_datetime || '',
        warningType: getRepresentativeWarningType(stations),
        causeEarthquake,
        targetAreaCount: new Set(stations.map(station => station.areacode).filter(Boolean)).size,
        maxHeightDisplay: buildMaxHeightDisplay(stations),
        stations,
        mapStations: stations.filter(station => station.hasCoordinate),
        hiddenStationCount: stations.filter(station => !station.hasCoordinate).length
    };
}

export function aggregateTsunamiRecords(payload) {
    const groups = new Map();

    parseTsunamiPayload(payload)
        .filter(record => record && typeof record === 'object')
        .forEach(record => {
            const eventid = String(record.eventid || record.xmlid || record.report_datetime || '');

            if (!groups.has(eventid)) {
                groups.set(eventid, []);
            }
            groups.get(eventid).push(record);
        });

    return Array.from(groups.values())
        .map(normalizeTsunamiEvent)
        .sort(sortTsunamis);
}

export async function loadTsunamis() {
    if (!configs.apiUrl.tsunami) {
        return [];
    }

    try {
        const response = await fetch(buildTsunamiRequestUrl(), {
            method: 'GET',
            cache: 'no-store',
            headers: buildApiRequestHeaders({
                'Content-Type': 'application/json'
            })
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        return aggregateTsunamiRecords(await response.json());
    } catch (error) {
        console.error('津波情報の取得に失敗しました:', error);
        return [];
    }
}

export function startTsunamiUpdates(callback, intervalMs = 60 * 1000) {
    loadTsunamis().then(callback);

    return setInterval(async () => {
        callback(await loadTsunamis());
    }, intervalMs);
}

export function stopTsunamiUpdates(intervalId) {
    if (intervalId) {
        clearInterval(intervalId);
    }
}
